/**
 * Penanda "dibaca" yang dibawa ketukan notifikasi HP (#1756) — MURNI, tanpa React, tanpa
 * `localStorage`, tanpa `window`.
 *
 * Dilaporkan Owner 24 Okt 2026: angka di bel kadang tetap ada walaupun notifikasi di HP sudah
 * diklik. "Tugas selesai" dihitung sejak penanda `terakhir-dilihat` (per peramban), dan penanda
 * itu sebelumnya HANYA maju saat panel bel dibuka — mengetuk notifikasi tidak menyentuhnya, jadi
 * angkanya bertahan sampai Owner kebetulan membuka bel.
 *
 * Jalurnya sekarang: push tugas membawa `dibacaSampai` (jam server) → sw.js menempelkannya ke URL
 * tujuan (`?dibaca=…`) atau, bila tab yang terbuka tidak dapat dinavigasi, mengirim pesan
 * `PESAN_DIBACA` → aplikasi memajukan penanda lewat `penandaBaru` di bawah.
 */

/** Nama parameter URL — HARUS sama dengan yang ditulis `public/sw.js`. */
export const PARAM_DIBACA = 'dibaca';

/** `type` pesan service worker → halaman — HARUS sama dengan yang ditulis `public/sw.js`. */
export const PESAN_DIBACA = 'workforce:pemberitahuan:dibaca';

/**
 * Seberapa jauh ke depan dari jam HP sebuah penanda masih diterima apa adanya.
 *
 * Penanda dari URL dapat dipalsukan (tautan `?dibaca=2099-…` akan membungkam bel selamanya di
 * peramban itu), dan jam HP dapat tertinggal dari jam server. Nilai yang lebih jauh dari batas
 * ini DIPOTONG ke batasnya, bukan ditolak — notifikasi lama tetap terbaca, dan arah gagalnya
 * adalah lencana yang muncul lagi, bukan lencana yang hilang diam-diam.
 */
export const BATAS_MASA_DEPAN_MS = 10 * 60_000;

/**
 * Nilai penanda yang baru, atau `null` bila penanda tidak perlu ditulis.
 *
 * Hanya MAJU, tidak pernah mundur: ketukan pada notifikasi lama tidak boleh memunculkan kembali
 * tugas yang sudah ditandai dibaca dari panel bel.
 */
export function penandaBaru(
  lamaIso: string | null,
  kandidatIso: string,
  sekarang: Date,
): string | null {
  const kandidat = Date.parse(kandidatIso);
  if (Number.isNaN(kandidat)) return null;

  const batas = sekarang.getTime() + BATAS_MASA_DEPAN_MS;
  const nilai = Math.min(kandidat, batas);

  const lama = lamaIso === null ? Number.NaN : Date.parse(lamaIso);
  if (!Number.isNaN(lama) && nilai <= lama) return null;

  return new Date(nilai).toISOString();
}

/**
 * Pisahkan penanda dari query string. `sisa` adalah query string TANPA parameter penanda
 * (string kosong, atau diawali `?`) — untuk `history.replaceState`, supaya memuat ulang halaman
 * atau membagikan tautannya tidak menerapkan penanda yang sama lagi.
 */
export function ambilPenandaDariSearch(search: string): {
  readonly dibacaSampai: string | null;
  readonly sisa: string;
} {
  const params = new URLSearchParams(search);
  const dibacaSampai = params.get(PARAM_DIBACA);
  if (dibacaSampai === null) return { dibacaSampai: null, sisa: search };

  params.delete(PARAM_DIBACA);
  const sisa = params.toString();
  return { dibacaSampai, sisa: sisa ? `?${sisa}` : '' };
}

export interface PesanDibaca {
  /** `null` = notifikasi tanpa penanda (pengajuan koreksi): hanya berpindah, tidak menandai. */
  readonly dibacaSampai: string | null;
  /** Path relatif tujuan klik, tanpa parameter penanda. */
  readonly url: string;
}

/**
 * Validasi pesan dari service worker. Apa pun yang bentuknya tidak persis → `null`, dan `url`
 * WAJIB path relatif satu-garis-miring: pesan ini berujung pada navigasi, dan `//host` atau
 * `https:` di sini akan membawa orang keluar dari aplikasi.
 */
export function bacaPesanDibaca(data: unknown): PesanDibaca | null {
  if (typeof data !== 'object' || data === null) return null;
  const d = data as Record<string, unknown>;
  if (d.type !== PESAN_DIBACA) return null;
  if (typeof d.url !== 'string' || !d.url.startsWith('/') || d.url.startsWith('//')) return null;
  if (d.dibacaSampai === null) return { dibacaSampai: null, url: d.url };
  if (typeof d.dibacaSampai !== 'string' || Number.isNaN(Date.parse(d.dibacaSampai))) return null;
  return { dibacaSampai: d.dibacaSampai, url: d.url };
}
